import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ClockIcon, MapPinIcon, PlaneIcon, BeachIcon, MountainIcon, CastleIcon, GlobeIcon, StarIcon } from '../components/Icons';
import api from '../services/api';
import SEO from '../components/SEO';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = ['All', 'Destinations', 'Travel Tips', 'Adventure', 'Culture', 'Food'];

  useEffect(() => {
    fetchBlogPosts();
  }, [selectedCategory]);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      const category = selectedCategory === 'All' ? 'All' : selectedCategory;
      const data = await api.getBlogPosts(category);
      setBlogPosts(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Failed to load blog posts. Please try again later.');
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  // Icon mapping for dynamic icon selection
  const iconMap = {
    PlaneIcon,
    BeachIcon,
    MountainIcon,
    CastleIcon,
    GlobeIcon,
    StarIcon,
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || GlobeIcon;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Filter is handled by the API, but we can also filter client-side if needed
  const filteredPosts = blogPosts;

  return (
    <div className="flex-1">
      <SEO
        title="Travel Blog"
        description="Travel stories, destination guides, and expert tips from Happy Yatra Travels. Discover India and beyond with our travel blog and insights."
        keywords="travel blog, India travel tips, destination guides, travel stories, travel insights"
        canonical="/blog"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-blue via-dark-blue to-primary-blue overflow-hidden py-20 md:py-32">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7z' fill='%23ffffff' fill-opacity='0.1'/%3E%3C/svg%3E")`,
              backgroundSize: '50px 50px'
            }}
          ></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full border border-white/30">
            <PlaneIcon className="w-5 h-5 text-yellow-300" />
            <span className="text-white font-semibold text-sm">Travel Blog & Insights</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight text-white">
            Travel Stories &
            <br />
            <span className="text-yellow-400">Expert Guides</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            Discover travel tips, destination guides, and inspiring stories from across India
          </p>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-primary-orange text-white shadow-lg transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
              <p className="mt-4 text-gray-600">Loading blog posts...</p>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {!loading && !error && filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-xl text-gray-600">No posts found in this category.</p>
            </div>
          )}

          {!loading && !error && filteredPosts.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => {
                const IconComponent = getIcon(post.icon_name || 'GlobeIcon');
                return (
                  <article
                    key={post.id}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image_url || 'https://images.unsplash.com/photo-1532386236358-a33d8a9434e3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-primary-orange text-white text-xs font-semibold rounded-full">
                          {post.category}
                        </span>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center gap-4 mb-3 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <ClockIcon className="w-4 h-4" />
                          <span>{post.read_time || '5 min read'}</span>
                        </div>
                        <span>•</span>
                        <span>{formatDate(post.published_date) || 'Recently'}</span>
                      </div>
                      
                      <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-blue transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      
                      <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                        {post.excerpt || post.content?.substring(0, 150) + '...'}
                      </p>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-blue to-primary-orange flex items-center justify-center">
                            <IconComponent className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{post.author}</span>
                        </div>
                        <Link
                          to={`/blog/${post.id}`}
                          className="text-primary-blue font-semibold text-sm hover:text-primary-orange transition-colors flex items-center gap-1"
                        >
                          Read More
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-primary-blue to-dark-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Plan Your Next Adventure?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Explore our comprehensive travel guides, insider tips, and destination insights to make your journey unforgettable. 
            Whether you're planning a beach getaway, mountain trek, or cultural exploration, our blog has everything you need 
            to create memories that last a lifetime.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/917718838808"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-primary-orange text-white rounded-xl font-semibold hover:bg-orange-600 transform hover:scale-105 transition-all duration-300 shadow-lg inline-flex items-center justify-center gap-2"
            >
              <PlaneIcon className="w-5 h-5" />
              Get Travel Consultation
            </a>
            <Link
              to="/packages"
              className="px-8 py-4 bg-white/20 backdrop-blur-md text-white rounded-xl font-semibold hover:bg-white/30 border-2 border-white/50 transform hover:scale-105 transition-all duration-300"
            >
              View Packages
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
